import { v4 as uuidv4 } from 'uuid';
import { normalizeHexColor } from './richText.js';
import { normalizeStoredImageItem, revokeLocalImageUrl } from './localImage.js';
import { LOCAL_ASSET } from './localAssets.js';
import { getMediaItemUrl } from './qiniuMedia.js';
import {
	formatAudioDuration,
	normalizeLocalAudioItem,
	revokeLocalAudioItem,
} from './localAudio.js';

export const AUDIO_PLAYER_TYPE = 'audio-player';
export const AUDIO_PLAYER_DEFAULT_BORDER_RADIUS = 12;
export const AUDIO_PLAYER_DEFAULT_WIDTH = 100;
export const AUDIO_PLAYER_DEFAULT_DURATION = 102;

const DEFAULT_TITLE_COLOR = '#ffffff';
const DEFAULT_SUBTITLE_COLOR = '#ffffff';
const DEFAULT_THEME_COLOR = '#ffffff';

// 默认音频文件的字节数（占位值，待替换为 sleep1.mp3 实际大小）
const AUDIO_PLAYER_DEFAULT_SIZE = Math.round(2.34 * 1024 * 1024); // ~4.17 MB
const AUDIO_PLAYER_DEFAULT_SIZE_LABEL = '2.34 MB';

function createDefaultCover() {
	const preset = LOCAL_ASSET.audioPlayerPresets || {};
	return {
		id: `audio-player-cover-${uuidv4()}`,
		filename: preset.coverFilename || 'cover.jpg',
		previewUrl: preset.coverUrl || LOCAL_ASSET.audioPlayerCover,
		url: '',
		local: false,
	};
}

function createDefaultAudio() {
	const preset = LOCAL_ASSET.audioPlayerPresets || {};
	const audioUrl = preset.audioUrl || '/gedit/audio/sleep1.mp3';
	return {
		// 优先使用 preset 显式声明的 id（如 dv-20011），否则回退到 uuid（用户上传时）
		id: preset.id || `audio-player-audio-${uuidv4()}`,
		filename: preset.filename || 'sleep1.mp3',
		filesize: preset.audioUrl ? AUDIO_PLAYER_DEFAULT_SIZE_LABEL : '',
		filesize_byte: preset.audioUrl ? AUDIO_PLAYER_DEFAULT_SIZE : 0,
		previewUrl: audioUrl,
		duration: preset.duration ?? AUDIO_PLAYER_DEFAULT_DURATION,
		durationText: formatAudioDuration(preset.duration ?? AUDIO_PLAYER_DEFAULT_DURATION),
		url: audioUrl,
		local: false,
	};
}

function createFallback() {
	return {
		title: '助眠音乐',
		subtitle: '助你一夜好眠',
		titleColor: DEFAULT_TITLE_COLOR,
		subtitleColor: DEFAULT_SUBTITLE_COLOR,
		themeColor: DEFAULT_THEME_COLOR,
		cover: createDefaultCover(),
		audio: createDefaultAudio(),
		darkOverlay: true,
		showCard: false,
		borderRadius: AUDIO_PLAYER_DEFAULT_BORDER_RADIUS,
		playerWidth: AUDIO_PLAYER_DEFAULT_WIDTH,
	};
}

export function getAudioPlayerDefaults(extra = {}) {
	return normalizeAudioPlayerData({ ...createFallback(), ...extra });
}

export function isAudioPlayerBlock(blockType) {
	return blockType === AUDIO_PLAYER_TYPE;
}

export function getAudioPlayerCoverUrl(data) {
	const image = normalizeAudioPlayerData(data).cover;
	return getMediaItemUrl(image);
}

/** @deprecated 使用 getAudioPlayerCoverUrl */
export function getAudioPlayerBgUrl(data) {
	return getAudioPlayerCoverUrl(data);
}

export function getAudioPlayerAudioUrl(data) {
	const audio = normalizeAudioPlayerData(data).audio;
	return getMediaItemUrl(audio);
}

function normalizeCoverImage(image, fallback) {
	if (!image?.previewUrl && !image?.url) {
		return fallback ? { ...fallback } : createDefaultCover();
	}
	return normalizeStoredImageItem(image, fallback);
}

function normalizeAudioAsset(audio, fallback) {
	if (!audio?.previewUrl && !audio?.url && !audio?.duration && !audio?.filename) {
		return fallback ? { ...fallback } : createDefaultAudio();
	}
	return normalizeLocalAudioItem({
		...fallback,
		...audio,
	});
}

export function normalizeAudioPlayerData(data = {}) {
	const fallback = createFallback();
	const width = Number(data.playerWidth);
	const sourceCover = data.cover ?? data.bgImage;
	return {
		title: data.title ?? fallback.title,
		subtitle: data.subtitle ?? fallback.subtitle,
		titleColor: normalizeHexColor(data.titleColor || fallback.titleColor),
		subtitleColor: normalizeHexColor(data.subtitleColor || fallback.subtitleColor),
		themeColor: normalizeHexColor(data.themeColor || fallback.themeColor),
		cover: normalizeCoverImage(sourceCover, fallback.cover),
		audio: normalizeAudioAsset(data.audio, fallback.audio),
		darkOverlay: data.darkOverlay !== false,
		showCard: data.showCard === true,
		borderRadius: Number.isFinite(Number(data.borderRadius))
			? Math.max(0, Number(data.borderRadius))
			: AUDIO_PLAYER_DEFAULT_BORDER_RADIUS,
		playerWidth: Number.isFinite(width) && width > 0
			? Math.min(100, Math.max(20, width))
			: AUDIO_PLAYER_DEFAULT_WIDTH,
	};
}

export function getAudioPlayerBlockStyle(data = {}, { h5 = false } = {}) {
	const normalized = normalizeAudioPlayerData(data);
	const pxRatio = h5 ? 50 : 1;
	const radius = normalized.borderRadius;

	return {
		borderRadius: h5 ? `${radius / pxRatio}rem` : `${radius}px`,
		playerWidth: `${normalized.playerWidth}%`,
		themeColor: normalized.themeColor,
		titleColor: normalized.titleColor,
		subtitleColor: normalized.subtitleColor,
		darkOverlay: normalized.darkOverlay,
		showCard: normalized.showCard,
	};
}

export function replaceAudioPlayerCover(currentCover, nextCover) {
	if (currentCover?.local) {
		revokeLocalImageUrl(currentCover.previewUrl);
	}
	return normalizeStoredImageItem(nextCover, currentCover);
}

/** @deprecated 使用 replaceAudioPlayerCover + 上传接口 */
export function replaceAudioPlayerBg(currentBg, file, createLocalImageItem) {
	if (currentBg?.local) {
		revokeLocalImageUrl(currentBg.previewUrl);
	}
	return createLocalImageItem(file);
}

export function replaceAudioPlayerAudio(currentAudio, nextAudio) {
	if (currentAudio?.local) {
		revokeLocalAudioItem(currentAudio);
	}
	return normalizeLocalAudioItem(nextAudio);
}

export function revokeAudioPlayerAssets(data = {}) {
	const normalized = normalizeAudioPlayerData(data);
	if (normalized.cover?.local) {
		revokeLocalImageUrl(normalized.cover.previewUrl);
	}
	if (normalized.audio?.local) {
		revokeLocalAudioItem(normalized.audio);
	}
}
